import { BehaviorSubject, catchError, firstValueFrom, forkJoin, of } from 'rxjs';
import { FilterModel, GridApi, IServerSideGetRowsParams } from '@ag-grid-community/core';
import { Injector } from '@angular/core';
import { LuceneExpression, toString as luceneToString } from 'lucene';
import moment from 'moment';

import { OrderService } from '../../../../order/services/order.service';
import { DatacenterSelectionService } from '../../../services/datacenter-selection.service';
import { OrderBy, OrderByDirection } from '../../../utils/constant';
import { Order, TotalGridFields } from '../../../../order/models/order';
import { PaginatedResult } from '../../../models/paginated-result';
import { formatTextDimension } from '../../../utils/number.format';
import { CommonGridOptions } from '../../grid-options';
import { ColDefWithFields, ColumnType } from '../../column-type';
import { FavoriteOrders } from '../../../../order/models/favorite-orders';
import { Strategy } from '../strategy';
import { FilterModel as FilterModelValue, FilterType } from '../../../models/ag-grid';
import { UserDatacenterView, DatacenterType } from '../../../models/datacenter-aggrid-view';
import { createDimensionFilter, createReferenceItemFilter, createNumberFilter,
	createDateFilter, createDateRangeFilter, createTextFilter, createDropDownFilter,
	createSuggestiveListFilter }
	from '../../../services/datacenter-filter';
import { DatacenterFilterUtilsService } from '../../../services/datacenter-filter-utils.service';
import { ExceptionReportService } from '../../../../order/services/exception-report.service';
import { UserService } from '../../../services/user.service';
import { DATE_FORMAT_PATTERN_DD_MM_YYYY, FilterDateFormatType,
	WEEKYEAR_FORMAT_PATTERN } from '../../../models/filter-formats';

export class StrategyOrder implements Strategy<Order> {
	orderService: OrderService;
	exceptionReportService: ExceptionReportService;
	userService: UserService;
	selectionService: DatacenterSelectionService;
	filterService: DatacenterFilterUtilsService;
	defaultOrderBy: OrderBy<keyof Order> = new OrderBy<keyof Order>('cdd', OrderByDirection.DESC);
	hasTotal = true;
	aggregateFields = [
		'netQtyAvailableInConsignment',
		'netQtyAvailableInTransit',
		'netQtyAvailableReadyToShip',
		'netQtyBlocked',
		'netQtyInProduction',
		'netQtyOrdered',
		'netQtyNotDelivered',
		'netQtyDelivered',
		'grossQtyAvailableInConsignment',
		'grossQtyAvailableInTransit',
		'grossQtyAvailableReadyToShip',
		'grossQtyBlocked',
		'grossQtyInProduction',
		'grossQtyOrdered',
		'grossQtyNotDelivered',
		'grossQtyDelivered'
	];

	constructor(injector: Injector) {
		this.orderService = injector.get<OrderService>(OrderService);
		this.exceptionReportService = injector.get<ExceptionReportService>(ExceptionReportService);
		this.selectionService = injector.get<DatacenterSelectionService>(DatacenterSelectionService);
		this.filterService = injector.get<DatacenterFilterUtilsService>(DatacenterFilterUtilsService);
		this.userService = injector.get<UserService>(UserService);
	}

	async getAllDataSelected(
		currentColumns: string[],
		filter: string | undefined,
		orderBy: OrderBy<keyof Order>[],
	) {
		return this.getAllData(orderBy, currentColumns, filter)
			.then((orders: PaginatedResult<Order>) => this.removeDeselectedRows(orders));
	}

	async getAllData(
		orderBy: OrderBy<keyof Order>[],
		currentColumns: string[],
		filter: string | undefined
	) {
		return firstValueFrom(this.orderService.getDatacenterOrders(
			<(keyof Order)[]> currentColumns,
			filter,
			0,
			60_000,
			orderBy,
			false
		));
	}

	formatCellByType(columnDef: ColDefWithFields<Order>, data: Order) {
		const field = columnDef.field || '';
		const fields = columnDef.fields || [];
		const { columnType } = columnDef;
		switch (columnType) {
		case ColumnType.DIMENSION_COLUMN:
			return formatTextDimension(
				<string> data[<keyof Order> fields[0]],
				<string> data[<keyof Order> fields[1]],
				<string> data[<keyof Order> fields[2]]
			);
		case ColumnType.ORDER_NUMBER_COLUMN:
		{
			let orderNumber = '';
			if (<string> data[<keyof Order> fields[0]]) {
				orderNumber = <string> data[<keyof Order> fields[0]];
			}
			if (<string> data[<keyof Order> fields[0]] && <string> data[<keyof Order> fields[1]]) {
				orderNumber += `/${ <string> data[<keyof Order> fields[1]] }`;
			}
			return orderNumber;
		}
		case ColumnType.NUMBER_COLUMN:
		{
			const value = <number | undefined >data[<keyof Order> field];
			return value === undefined ? '' : String(value);
		}
		case ColumnType.TEXT_COLUMN:
			return this.formatingTextColumn(field, columnDef.colId, data);
		case ColumnType.TRANSLATED_COLUMN:
			// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
			return <string> data[<keyof Order> columnDef.filterParams.orderFields[1]];
		case ColumnType.DATE_COLUMN:
			if (data[<keyof Order> field]) {
				const dateformat = (<FilterDateFormatType> columnDef.cellRendererParams).outputFormat === 'week'
					? WEEKYEAR_FORMAT_PATTERN : DATE_FORMAT_PATTERN_DD_MM_YYYY;
				const weekData = dateformat === WEEKYEAR_FORMAT_PATTERN && data[<keyof Order> field] === '1900-W01'
					? '' : data[<keyof Order> field];
				return weekData === '' ? '' : moment.utc(<string> weekData).format(dateformat);
			}
			return '';
		case ColumnType.COLOUR_COLUMN:
			if (data[<keyof Order>field]) {
				const value = <string>data[<keyof Order>field];
				const colorMapping: Record<string, string> = {
					// eslint-disable-next-line @typescript-eslint/naming-convention
					1: 'FF008000', 2: 'FFFFA500', 3: 'FFFF0000',
				};
				return colorMapping[value];
			}
			return 'FFFFFFFF';
		default:
			throw new Error('unknown filter type');
		}
	}

	getCustomGridOptions(defaultGridOptions: CommonGridOptions) {
		return new CommonGridOptions<Order>({
			...defaultGridOptions,
			getRowId: ({ data }: { data: Order }) => `${ data.supplierOrderNumber }${ data.supplierOrderItemNumber }`,
		});
	}

	getDataFromGrid(
		orderBy: OrderBy<keyof Order>[],
		currentColumns: (keyof Order)[],
		filter: string | undefined,
		params: IServerSideGetRowsParams<Order>,
		rowsCountSubject: BehaviorSubject<number | undefined>,
	) {
		const currentColumnsWithId = this.addIdColumn(currentColumns);
		const getOrders = this.orderService.getDatacenterOrders(
			currentColumnsWithId,
			filter,
			params.request.startRow ?? 0,
			250,
			orderBy,
			false
		);
		const getFavoriteOrders = this.orderService.getFavoriteOrders();
		return forkJoin([
			getOrders,
			getFavoriteOrders.pipe(
				catchError((err: unknown) => {
					console.error(err);
					return of([]);
				})
			),
		]).subscribe({
			next: ([orders, favoriteOrders]) => {
				orders.results = this.setFavoriteOrders(orders.results, favoriteOrders);
				const lastRow = params.request.endRow === undefined || orders.count <= params.request.endRow
					? orders.count
					: -1;
				params.success({ rowData: orders.results, rowCount: lastRow });
				rowsCountSubject.next(orders.count);
			},
			error: (err: unknown) => {
				console.error('error requesting order datacenter', err);
				rowsCountSubject.next(undefined);
				params.fail();
			}
		});
	}

	formatingTextColumn(field: string, colId: string | undefined, data?: Order) {
		if (!colId || data === undefined) {
			return '';
		}
		switch (colId) {
		case 'comments':
			if (!data.comments) {
				return '';
			}
			return data.comments.map((item, index) => {
				if (data.comments && index < data.comments.length - 1) {
					return `${ this.buildYearWeek(item.yearWeek) } : ${ item.comment }\n`;
				}
				return `${ this.buildYearWeek(item.yearWeek) } : ${ item.comment }`;
			}).join('');
		case 'answers':
			if (!data.answers) {
				return '';
			}
			return data.answers.map((item, index) => {
				if (data.answers && index < data.answers.length - 1) {
					return `${ this.buildYearWeek(item.yearWeek) } : ${ item.comment }\n`;
				}
				return `${ this.buildYearWeek(item.yearWeek) } : ${ item.comment }`;
			}).join('');
		case 'recentComments':
			if (!data.recentComments) {
				return '';
			}
			return data.recentComments.map((item, index) => {
				if (data.recentComments && index < data.recentComments.length - 1) {
					return `${ this.buildYearWeek(item.yearWeek) } : ${ item.comment }\n`;
				}
				return `${ this.buildYearWeek(item.yearWeek) } : ${ item.comment }`;
			}).join('');
		case 'recentAnswers':
			if (!data.recentAnswers) {
				return '';
			}
			return data.recentAnswers.map((item, index) => {
				if (data.recentAnswers && index < data.recentAnswers.length - 1) {
					return `${ this.buildYearWeek(item.yearWeek) } : ${ item.comment }\n`;
				}
				return `${ this.buildYearWeek(item.yearWeek) } : ${ item.comment }`;
			}).join('');
		}
		return <string>data[<keyof Order>field];
	}

	buildYearWeek(yearWeek: string) {
		const year = yearWeek.slice(0, yearWeek.indexOf('W'));
		const week = yearWeek.slice(yearWeek.indexOf('W') + 1);

		return `Week ${ week } ${ year }`;
	}

	setFavoriteOrders(orders: Order[], favoriteOrders: FavoriteOrders[]) {
		return orders.map(order => {
			const comparedFavoriteNumber = favoriteOrders.find(
				favoriteOrder => favoriteOrder.supplierOrderItem === order.supplierOrderItemNumber
					&& favoriteOrder.supplierOrderReference === order.supplierOrderNumber
			);
			order.favorite = comparedFavoriteNumber;
			order.isFavorite = Boolean(comparedFavoriteNumber);

			return order;
		});
	}

	addIdColumn(currentColumns: (keyof Order)[]) {
		if (!currentColumns.includes('supplierOrderNumber')) {
			currentColumns.push('supplierOrderNumber');
		}
		if (!currentColumns.includes('supplierOrderItemNumber')) {
			currentColumns.push('supplierOrderItemNumber');
		}
		return currentColumns;
	}

	createAggregateCustomViewFilter(view: UserDatacenterView) {
		if (this.filterService.getLuceneFromAllFilters(view)) {
			const criterionFilterNode = this.filterService.getLuceneFromAllFilters(view);
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			return luceneToString(criterionFilterNode!);
		}
		return '';
	}

	setTotal(api?: GridApi, filter?: string) {
		return this.orderService.getAggregatedQuantity(filter)
			.subscribe({
				next: value => {
					const totalGridFields: TotalGridFields = {};
					totalGridFields.netQtyBlocked = value.netQtyBlocked;
					totalGridFields.grossQtyOrdered = value.qtyOrdered;
					totalGridFields.netQtyDelivered = value.netQtyDelivered;
					totalGridFields.netQtyAvailableInTransit = value.netQtyAvailableInTransit;
					totalGridFields.netQtyAvailableReadyToShip = value.netQtyAvailableReadyToShip;
					totalGridFields.grossQtyInProduction = value.qtyInProduction;
					totalGridFields.netQtyAvailableInConsignment = value.netQtyAvailableInConsignment;
					totalGridFields.grossQtyBlocked = value.grossQtyBlocked;
					totalGridFields.grossQtyDelivered = value.grossQtyDelivered;
					totalGridFields.grossQtyAvailableInTransit = value.grossQtyAvailableInTransit;
					totalGridFields.grossQtyAvailableReadyToShip = value.grossQtyAvailableReadyToShip;
					totalGridFields.grossQtyAvailableInConsignment = value.grossQtyAvailableInConsignment;
					totalGridFields.grossQtyNotDelivered = value.grossQtyNotDelivered;
					totalGridFields.netQtyNotDelivered = value.netQtyNotDelivered;

					api?.setGridOption('pinnedTopRowData', [totalGridFields]);
				},
				error: (err: unknown) => {
					console.error('error loading', err);
				}
			});
	}

	createAggregateQuickSearchFilter(
		currentSelectedView: UserDatacenterView,
		api?: GridApi,
		filterModel?: FilterModel
	) {
		let criterionFilterNode;
		let filter = '';
		if (!filterModel) {
			return filter;
		}
		for (const [columnId, filterModel1] of Object.entries(<Record<string, FilterModelValue>> filterModel)) {
			const field = <DatacenterType | undefined> api?.getColumnDef(columnId)?.field;
			const colId = <DatacenterType | undefined> api?.getColumnDef(columnId)?.colId;
			const { fields } = <ColDefWithFields> api?.getColumnDef(columnId);
			let criterionFilterValueNode;
			switch (filterModel1.customFilterName) {
			case FilterType.DIMENSION_FILTER:
				criterionFilterValueNode = createDimensionFilter(filterModel1);
				break;
			case FilterType.ORDER_NUMBER_FILTER:
				criterionFilterValueNode = createReferenceItemFilter(filterModel1, fields);
				break;
			case FilterType.NUMBER_FILTER:
				criterionFilterValueNode = createNumberFilter(filterModel1, field);
				break;
			case FilterType.DATE_FILTER:
				criterionFilterValueNode = createDateFilter(filterModel1.date, field);
				break;
			case FilterType.DROPDOWN_FILTER:
				criterionFilterValueNode = createDropDownFilter(filterModel1.text, colId);
				break;
			case FilterType.SUGGESTIVE_LIST_FILTER:
				criterionFilterValueNode = createSuggestiveListFilter(filterModel1.text, colId);
				break;
			case FilterType.DATE_RANGE_FILTER:
				criterionFilterValueNode = createDateRangeFilter(filterModel1, field);
				break;
			case FilterType.TEXT_FILTER:
				criterionFilterValueNode = createTextFilter(filterModel1, field);
				break;
			default:
				throw new Error('unknown filter type');
			}
			if (!criterionFilterNode) {
				criterionFilterNode = criterionFilterValueNode;
				continue;
			}
			criterionFilterNode = {
				left: criterionFilterNode,
				operator: 'AND',
				right: criterionFilterValueNode,
				parenthesized: false
			};
		}
		if (this.filterService.getLuceneFromAllFilters(currentSelectedView)) {
			criterionFilterNode = !criterionFilterNode
				? this.filterService.getLuceneFromAllFilters(currentSelectedView)
				: {
					left: criterionFilterNode,
					operator: 'AND',
					right: this.filterService.getLuceneFromAllFilters(currentSelectedView),
					parenthesized: true,
				};
		}
		filter = luceneToString(<LuceneExpression> criterionFilterNode);
		return filter;
	}

	private removeDeselectedRows(orders: PaginatedResult<Order>) {
		return orders.results.filter(order => {
			const rowDeselected = this.selectionService.findDeselectedNode(
				`${ order.supplierOrderNumber }${ order.supplierOrderItemNumber }`
			);
			return !rowDeselected;
		});
	}
}
